import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, CalendarDays, Clock, User, Bell, Trophy, Medal,
  Dumbbell, Users, MessageSquare, ArrowLeftRight, MessageCircle,
  PanelLeftClose, PanelLeftOpen, GraduationCap,
} from "lucide-react";
import OpenMatchIcon from "@/components/icons/OpenMatchIcon";
import { cn } from "@/lib/utils";
import { isNavActive } from "@/lib/navActive";
import { useUserRole } from "@/hooks/useUserRole";
import { useOrganization } from "@/hooks/useOrganization";
import { useAppMode } from "@/hooks/useAppMode";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useTotalUnreadCount } from "@/hooks/useTotalUnreadCount";
import { useUnreadNotificationsCount } from "@/hooks/useUnreadNotificationsCount";
import { useAuth } from "@/hooks/useAuth";
import { useTranslation } from "react-i18next";

const STORAGE_KEY = "sidebar-collapsed";

interface NavItem {
  to: string;
  icon: any;
  labelKey: string;
  feature?: string;
  /** which unread counter feeds this item's badge */
  badge?: "messages" | "notifications";
}

const groups: { labelKey: string; items: NavItem[] }[] = [
  {
    labelKey: "sidebarGroups.play",
    items: [
      // G-098: explicit Hem entry (the logo link alone is not discoverable wayfinding).
      { to: "/", icon: Home, labelKey: "nav.home" },
      { to: "/book", icon: CalendarDays, labelKey: "nav.bookCourt", feature: "bookings" },
      { to: "/play", icon: OpenMatchIcon, labelKey: "nav.play", feature: "open_matches" },
    ],
  },
  {
    labelKey: "sidebarGroups.club",
    items: [
      { to: "/leagues", icon: Trophy, labelKey: "nav.leagues", feature: "leagues" },
      // G-096: player-facing tournaments entry, gated like the manager nav.
      { to: "/tournaments", icon: Medal, labelKey: "nav.tournaments", feature: "tournaments" },
      { to: "/training", icon: Dumbbell, labelKey: "nav.training", feature: "training" },
      // UPPDRAG-34: gate "Spelare" by the players feature flag. Default
      // remains true so existing orgs see no change; halls that don't
      // want the directory exposed can toggle it off via Inställningar
      // → Funktioner → Spelare.
      { to: "/players", icon: Users, labelKey: "nav.players", feature: "players" },
      { to: "/community", icon: MessageCircle, labelKey: "nav.community", feature: "community" },
    ],
  },
  {
    labelKey: "sidebarGroups.myAccount",
    items: [
      { to: "/bookings", icon: Clock, labelKey: "nav.myBookings" },
      { to: "/messages", icon: MessageSquare, labelKey: "nav.messages", badge: "messages", feature: "chat" },
      { to: "/notifications", icon: Bell, labelKey: "nav.notifications", badge: "notifications" },
      { to: "/profile", icon: User, labelKey: "nav.profile" },
    ],
  },
];

export default function DesktopSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, roles, isLoading: roleLoading } = useUserRole();
  const { organization } = useOrganization();
  const { user } = useAuth();
  const { setMode } = useAppMode();
  const { isEnabled } = useFeatureFlags();
  const { t } = useTranslation("common");

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed));
    } catch {
      // localStorage unavailable
    }
  }, [collapsed]);

  const messagesUnread = useTotalUnreadCount();
  const notificationsUnread = useUnreadNotificationsCount();

  const switchToManager = () => {
    setMode("manager");
    navigate("/manager/dashboard");
  };

  return (
    <aside
      className={cn(
        "sticky top-0 flex h-screen flex-col border-r bg-card transition-[width] duration-200",
        collapsed ? "lg:w-16" : "lg:w-60",
        "w-60", // mobile always full width when sidebar is shown
      )}
    >
      <Link
        to="/"
        className="flex flex-col items-center justify-center border-b px-5 gradient-sidebar hover:opacity-90 transition-opacity"
      >
        {(() => {
          const sidebarDisplay = (organization?.settings as any)?.sidebar_display ??
            (organization?.logo_url ? "logo" : "name");

          if (collapsed) {
            const iconUrl = (organization as any)?.app_icon_url || organization?.logo_url;
            return (
              <div className="flex items-center justify-center h-16">
                {iconUrl ? (
                  <img src={iconUrl} alt={organization?.name ?? ""} className="h-10 w-10 rounded-lg object-contain" />
                ) : (
                  <Home className="h-5 w-5" />
                )}
              </div>
            );
          }

          if (sidebarDisplay === "logo" && organization?.logo_url) {
            return (
              <div className="flex flex-col items-center gap-1 py-4">
                <img src={organization.logo_url} alt={organization.name ?? ""} className="h-10 max-w-[160px] object-contain" />
              </div>
            );
          }
          return (
            <div className="flex items-center gap-2 h-16 self-start">
              {organization?.logo_url && (
                <img src={organization.logo_url} alt="" className="h-8 w-8 rounded-md object-cover" />
              )}
              <span className="truncate text-sm font-semibold">{organization?.name ?? "Klubben"}</span>
            </div>
          );
        })()}
      </Link>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {groups.map((group) => {
          const visibleItems = group.items.filter(
            (item) => !item.feature || isEnabled(item.feature)
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.labelKey}>
              {!collapsed && (
                <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-widest text-muted-foreground/60 uppercase">
                  {t(group.labelKey)}
                </p>
              )}
              <div className="space-y-0.5">
                {visibleItems.map(({ to, icon: Icon, labelKey, badge }) => {
                  const active = isNavActive(location.pathname, to);
                  const unreadCount = badge === "messages" ? messagesUnread
                    : badge === "notifications" ? notificationsUnread
                    : 0;
                  return (
                    <Link
                      key={to}
                      to={to}
                      title={collapsed ? t(labelKey) : undefined}
                      className={cn(
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                        collapsed && "justify-center px-0",
                        active
                          ? "bg-primary/10 text-primary font-medium border-l-[3px] border-primary"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground border-l-[3px] border-transparent"
                      )}
                    >
                      <span className="relative">
                        <Icon className="h-4.5 w-4.5" />
                        {badge && unreadCount > 0 && collapsed && (
                          <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive text-[8px] font-bold text-destructive-foreground px-0.5">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </span>
                      {!collapsed && (
                        <>
                          <span className="flex-1">{t(labelKey)}</span>
                          {badge && unreadCount > 0 && (
                            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground px-1">
                              {unreadCount > 99 ? "99+" : unreadCount}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Coach link — only for users with coach role */}
        {roles.includes("coach") && (
          <div>
            {!collapsed && (
              <p className="px-3 mb-1.5 text-[10px] font-semibold tracking-widest text-muted-foreground/60 uppercase">
                {t("nav.coach")}
              </p>
            )}
            <Link
              to="/coach"
              title={collapsed ? t("nav.coachDashboard") : undefined}
              className={cn(
                "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                collapsed && "justify-center px-0",
                location.pathname.startsWith("/coach")
                  ? "bg-primary/10 text-primary font-medium border-l-[3px] border-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground border-l-[3px] border-transparent"
              )}
            >
              <GraduationCap className="h-4.5 w-4.5" />
              {!collapsed && <span>{t("nav.coachDashboard")}</span>}
            </Link>
          </div>
        )}
      </nav>

      {(isAdmin || roleLoading) && (
        <div className="border-t p-3">
          <button
            onClick={switchToManager}
            disabled={roleLoading}
            title={collapsed ? t("nav.switchToAdmin") : undefined}
            className={cn(
              "flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50",
              collapsed && "justify-center px-0",
            )}
          >
            <ArrowLeftRight className="h-4 w-4" />
            {!collapsed && <span>{t("nav.switchToAdmin")}</span>}
          </button>
        </div>
      )}

      {/* Collapse toggle — only visible on desktop */}
      <div className="hidden lg:block border-t p-3">
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className="flex w-full items-center justify-center rounded-md py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </div>
    </aside>
  );
}
